import { SatelliteWebLandlordPropertyHandler_search } from '@zg-rentals/ts-satellite-client';
import type { SatelliteWebLandlordPropertySearchRequestDto } from '@zg-rentals/ts-satellite-client';
import { ContextError } from '@zg-rentals/log-error';
import { is4xxError } from '../utils/errorUtils';

/**
 * Shared infinite query options for property search.
 * Used by usePropertyFilter (full property selector) and useHasProperties (lightweight ownership check)
 * so React Query deduplicates the underlying network request.
 */
export default function getPropertySearchQueryOptions(searchText = '') {
  return {
    queryKey: ['propertySearch', searchText],
    // Both observers report through the provider's global query-error handler, which merges this
    // context into the log. It lives here rather than in either hook so the failed request's own
    // inputs are what gets reported, whichever observer triggered the fetch.
    meta: {
      tags: ['satellite:propertySearch'],
      context: {
        api: 'propertySearch',
        details: 'propertySearchQueryOptions - Error fetching property listings',
        searchText,
      },
    },
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const requestData: SatelliteWebLandlordPropertySearchRequestDto = {
        startKeyExclusive: pageParam,
      };

      if (searchText) {
        requestData.searchText = searchText;
      }

      const response = await SatelliteWebLandlordPropertyHandler_search(requestData);

      // The transport casts a non-JSON 2xx (auth redirect, CDN error page) straight to the DTO, so a
      // page can reach the cache with no `listings`. Fail the query instead of caching it — a resolved
      // malformed page looks identical to an empty one, so it would strand the user on an empty list
      // and stop pagination with no signal. Failing routes it into the error path every observer
      // already handles, and the global query-error reporter logs it once with the context attached
      // here — the only place the status and content-type that produced it are still visible.
      if (!Array.isArray(response?.listings)) {
        const rawResponse = response instanceof Response ? response : undefined;
        // `reason` rather than `details`: logError spreads the reporter's context (this query's
        // `meta.context`) after the error's own, so a `details` key here would be overwritten by the
        // generic one above and never reach Splunk.
        throw new ContextError('PropertySearchPageMissingListings', {
          reason: 'propertySearchQueryOptions - property search page missing listings',
          status: rawResponse?.status,
          contentType: rawResponse?.headers.get('content-type'),
        });
      }

      return response;
    },
    getNextPageParam: (lastPage: Awaited<ReturnType<typeof SatelliteWebLandlordPropertyHandler_search>>) => {
      return lastPage?.pager?.hasMore ? lastPage.pager.endKey : undefined;
    },
    initialPageParam: undefined as string | undefined,
    // Retry transient failures, but never a 4xx: a client error (e.g. the 400 for an over-long
    // searchText) is deterministic, so retrying only multiplies the doomed request. Lives here so
    // every observer of this shared query (usePropertyFilter, useHasProperties) gets the guarantee.
    retry: (failureCount: number, error: unknown) => !is4xxError(error) && failureCount < 2,
  };
}
